"use client";

// ===========================================
// konakla - Google Analytics 4 (rıza kapılı)
// ===========================================
//
// KURAL: Kullanıcı çerez banner'ında ANALİTİK çerezlere açıkça izin vermediyse
// gtag.js HİÇ yüklenmez, tek bir istek bile gitmez.
//
// Bu bir tercih değil, zorunluluk: /cookies ve /kvkk sayfalarında "zorunlu
// olmayan çerezleri yalnızca izninizle kullanırız" ve "izninizi dilediğiniz
// zaman geri alabilirsiniz" taahhütleri yazılı. GA'yı rızaya bağlamadan açmak
// bu metinleri ihlal eder.
//
// ⚠️ Rıza GERİ ALINDIĞINDA bileşeni kaldırmak TEK BAŞINA YETMEZ:
// next/script sayfaya enjekte ettiği gtag.js'i unmount'ta kaldırmaz; window.gtag
// ve dataLayer bellekte, _ga çerezleri tarayıcıda kalır. Bu yüzden geri alma
// yolunda Google'ın resmî kill-switch'i (`ga-disable-<ID>`) set edilir ve
// çerezler süresi doldurularak silinir.

import Script from "next/script";
import { useEffect, useState } from "react";

const CONSENT_STORAGE_KEY = "konakla:cookie-consent";
const CONSENT_VERSION = 1;
/** Çerez politikasında yazılı taahhüt: en fazla 13 ay. GA4 varsayılanı bunu aşar. */
const COOKIE_EXPIRES_SECONDS = 34_190_000; // ~395 gün

interface ConsentState {
  version: number;
  analytics?: boolean;
}

function analyticsAllowed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as ConsentState;
    return parsed.version === CONSENT_VERSION && parsed.analytics === true;
  } catch {
    return false;
  }
}

/** Rıza geri alındığında toplamayı GERÇEKTEN durdurur. */
function disableTracking(measurementId: string) {
  if (typeof window === "undefined") return;

  // 1) Google'ın resmî kill-switch'i — yüklü gtag.js artık hiçbir şey göndermez.
  (window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = true;

  // 2) Yazılmış çerezleri sil (_ga ve _ga_<ID>). Alan adının nokta'lı biçimi de
  //    denenir: GA çerezi ".konakla.co" domain'iyle yazar.
  const host = window.location.hostname;
  const domains = [host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];
  for (const name of document.cookie.split(";").map((c) => c.split("=")[0].trim())) {
    if (!name.startsWith("_ga")) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
    }
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
}

/**
 * Ölçüm kimliği doğrudan `process.env`'den okunur; Next.js NEXT_PUBLIC_* değerlerini
 * derleme sırasında yerine yazar.
 * ⚠️ `lib/env.ts` BİLEREK kullanılmadı: o modül servis-rol anahtarı, PayTR salt'ı
 * gibi sunucu sırlarını okuyor ve istemci bileşenine hiç girmemeli.
 */
const MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_ID || "";

export function GoogleAnalytics() {
  const measurementId = MEASUREMENT_ID;

  // SSR ve ilk render'da HER ZAMAN false → hydration uyuşmazlığı olmaz,
  // rıza yalnızca tarayıcıda okunabilir.
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (!measurementId) return;

    const sync = () => {
      const next = analyticsAllowed();
      setAllowed((prev) => {
        // İzin VARDI, artık YOK → toplamayı fiilen durdur.
        if (prev && !next) disableTracking(measurementId);
        return next;
      });
    };

    sync();

    // Aynı sekmede banner'dan gelen değişiklik…
    window.addEventListener("konakla:consent-updated", sync);
    // …ve başka sekmede yapılan değişiklik.
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("konakla:consent-updated", sync);
      window.removeEventListener("storage", sync);
    };
  }, [measurementId]);

  // Ölçüm kimliği girilmemişse (ör. Vercel'de env henüz set edilmedi) veya
  // kullanıcı izin vermediyse hiçbir şey render edilmez.
  if (!measurementId || !allowed) return null;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
        strategy="afterInteractive"
      />
      <Script id="konakla-gtag-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${measurementId}', {
            cookie_expires: ${COOKIE_EXPIRES_SECONDS},
            cookie_flags: 'SameSite=Lax;Secure',
            // Çerez banner'ı ANALİTİK ve PAZARLAMA iznini AYRI AYRI soruyor.
            // Bu bileşen yalnızca ANALİTİK iznine bakarak yükleniyor; dolayısıyla
            // GA4'ün varsayılan reklam sinyalleri AÇIK kalsaydı, "analitik evet /
            // pazarlama hayır" diyen kullanıcı için banner'da verilen söz ile
            // fiili davranış ayrışırdı. İkisi de kapatılıyor.
            allow_google_signals: false,
            allow_ad_personalization_signals: false
          });
        `}
      </Script>
    </>
  );
}
